import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { Modal } from '@/components/common';
import CuttingOrderForm, { CuttingOrderFormRef } from '../CuttingOrderForm';
import { useUIStore } from '@/store/uiStore';
import { cuttingApi } from '@/api/endpoints/cutting';
import { CreateCuttingFormRequest, UpdateCuttingFormRequest, CuttingFormResponse, CuttingFormStatus } from '@/types/cutting';
import { toast } from '@/store/toastStore';

const CreateCuttingOrderModal: React.FC = () => {
  const { t } = useTranslation();
  const { modals, closeModal, selectedCuttingOrder, isViewOnly, refreshCallbacks } = useUIStore();
  const [loading, setLoading] = useState(false);
  const [cuttingOrder, setCuttingOrder] = useState<CuttingFormResponse | null>(null);
  const formRef = useRef<CuttingOrderFormRef>(null);

  const isOpen = modals.createCuttingOrder;
  const isEditing = !!selectedCuttingOrder;

  // Fetch cutting order for editing
  useEffect(() => {
    if (isOpen && selectedCuttingOrder) {
      fetchCuttingOrder();
    } else if (isOpen && !selectedCuttingOrder) {
      setCuttingOrder(null);
    }
  }, [isOpen, selectedCuttingOrder]);

  const fetchCuttingOrder = async () => {
    if (!selectedCuttingOrder) return;

    setLoading(true);
    try {
      const order = await cuttingApi.getCuttingForm(selectedCuttingOrder);
      setCuttingOrder(order);
    } catch (error) {
      console.error('Error fetching cutting form:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (data: CreateCuttingFormRequest | UpdateCuttingFormRequest) => {
    setLoading(true);
    try {
      let cuttingForm;

      if (isEditing) {
        cuttingForm = await cuttingApi.updateCuttingForm(selectedCuttingOrder!, data as UpdateCuttingFormRequest);
        toast.success(t('cuttingOrders.messages.updateSuccess'));
        console.log('Cutting form updated successfully');

        // Refresh the cutting order data after update to reflect changes
        await fetchCuttingOrder();

        // Notify parent to refresh data
        refreshCallbacks.cuttingOrders?.();
      } else {
        cuttingForm = await cuttingApi.createCuttingForm(data as CreateCuttingFormRequest);
        toast.success(t('cuttingOrders.messages.createSuccess'));
        console.log('Cutting form created successfully');

        // Notify parent to refresh data
        refreshCallbacks.cuttingOrders?.();
      }

      // Handle cutting form lines if provided (only for new forms, not when editing)
      if (!isEditing && (data as any).cuttingFormLines && (data as any).cuttingFormLines.length > 0) {
        const cuttingFormLines = (data as any).cuttingFormLines.map((line: any) => ({
          cutting_form_id: cuttingForm.code,
          raw_material_id: line.materialId,
          cutting_config_id: line.cuttingConfigId || 'DEFAULT_CONFIG',
          cutting_type: line.cuttingType || 1, // Include cutting type from the form
          material_category_group_id: line.materialCategoryGroupId || 'DEFAULT_GROUP', // Use actual material category group ID
          input_qty: line.input_qty || line.inputQty || 1, // Use input_qty first, fallback to inputQty, then default to 1
        }));

        try {
          await cuttingApi.createManyCuttingFormLines(cuttingFormLines);
          console.log('Cutting form lines created successfully');
        } catch (lineError) {
          console.error('Error creating cutting form lines:', lineError);
          // Show specific error message for cutting form lines
          const errorMessage = lineError instanceof Error ? lineError.message : 'Unknown error occurred';
          toast.error(t('cuttingOrders.messages.createLinesError', { error: errorMessage }));
          // Continue even if lines fail - user can add them later
        }
      }
      // Note: When editing, individual cutting form line operations are handled within CuttingOrderForm

      // Clear form after successful save (only for new forms)
      if (!isEditing && formRef.current) {
        formRef.current.resetForm();
      }

      // Don't close modal, allow user to continue working

      // In a real app, you would refresh the cutting form list here

    } catch (error) {
      console.error(`Error ${isEditing ? 'updating' : 'creating'} cutting form:`, error);
      // Show specific error message from API if available
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      const baseMessage = isEditing ? t('cuttingOrders.messages.updateError') : t('cuttingOrders.messages.createError');
      toast.error(`${baseMessage}: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAndClose = async (data: CreateCuttingFormRequest | UpdateCuttingFormRequest) => {
    setLoading(true);
    try {
      let cuttingForm;

      if (isEditing) {
        cuttingForm = await cuttingApi.updateCuttingForm(selectedCuttingOrder!, data as UpdateCuttingFormRequest);
        toast.success(t('cuttingOrders.messages.updateSuccess'));
        console.log('Cutting form updated successfully');
      } else {
        cuttingForm = await cuttingApi.createCuttingForm(data as CreateCuttingFormRequest);
        toast.success(t('cuttingOrders.messages.createSuccess'));
        console.log('Cutting form created successfully');
      }

      // Notify parent to refresh data
      refreshCallbacks.cuttingOrders?.();

      // Close modal after successful save
      handleCancel();

      // Handle cutting form lines if provided (only for new forms, not when editing)
      if (!isEditing && (data as any).cuttingFormLines && (data as any).cuttingFormLines.length > 0) {
        const cuttingFormLines = (data as any).cuttingFormLines.map((line: any) => ({
          cutting_form_id: cuttingForm.code,
          raw_material_id: line.materialId,
          cutting_config_id: line.cuttingConfigId || 'DEFAULT_CONFIG',
          cutting_type: line.cuttingType || 1, // Include cutting type from the form
          material_category_group_id: line.materialCategoryGroupId || 'DEFAULT_GROUP', // Use actual material category group ID
          input_qty: line.input_qty || line.inputQty || 1, // Use input_qty first, fallback to inputQty, then default to 1
        }));

        try {
          await cuttingApi.createManyCuttingFormLines(cuttingFormLines);
          console.log('Cutting form lines created successfully');
        } catch (lineError) {
          console.error('Error creating cutting form lines:', lineError);
          // Continue even if lines fail - user can add them later
        }
      }
      // Note: When editing, individual cutting form line operations are handled within CuttingOrderForm

      // Clear form after successful save and close (only for new forms)
      if (!isEditing && formRef.current) {
        formRef.current.resetForm();
      }

      // Close modal and trigger refresh of main table data
      handleCancel();

      // Trigger a page refresh or callback to refresh the main table data
      // This would typically be handled by the parent component
      if (window.location.pathname.includes('/cutting')) {
        window.location.reload();
      }

    } catch (error) {
      console.error(`Error ${isEditing ? 'updating' : 'creating'} cutting form:`, error);
      toast.error(isEditing ? t('cuttingOrders.messages.updateError') : t('cuttingOrders.messages.createError'));
    } finally {
      setLoading(false);
    }
  };

  const handleStatusTransition = async (newStatus: CuttingFormStatus) => {
    // Refresh the cutting order data after status transition
    if (selectedCuttingOrder) {
      await fetchCuttingOrder();
    }

    // Notify parent to refresh data
    refreshCallbacks.cuttingOrders?.();

    // Show success message and close modal for certain status transitions
    if (newStatus === 'cut') {
      toast.success(t('cuttingOrders.messages.statusUpdateSuccess'));
      handleCancel(); // Close modal after successful cut status transition
    }
  };

  const handleCancel = () => {
    closeModal('createCuttingOrder');
    setCuttingOrder(null);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCancel}
      title={isViewOnly ? t('cuttingOrders.actions.view') : (isEditing ? t('cuttingOrders.actions.edit') : t('cuttingOrders.actions.create'))} // Title positioned top-left of modal as specified
      size="screen-2/3" // Modal wrapper occupies 2/3 of screen width as specified
      closeOnOverlayClick={!loading}
      closeOnEscape={!loading}
    >
      <CuttingOrderForm
        ref={formRef}
        cuttingOrder={cuttingOrder || undefined}
        onSubmit={handleSubmit}
        onSaveAndClose={handleSaveAndClose}
        onCancel={handleCancel}
        onStatusTransition={handleStatusTransition}
        loading={loading}
        isViewOnly={isViewOnly}
      />
    </Modal>
  );
};

export default CreateCuttingOrderModal;
